import { NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useSpring, useMotionValue } from "framer-motion";
import { Menu, X, Globe, Sun, Moon } from "lucide-react";
import { FaWhatsapp, FaTiktok, FaInstagram, FaLinkedinIn } from "react-icons/fa";
import { useState, useEffect } from "react";
import { useLangStore } from "@/lib/langStore";
import { translations } from "@/lib/i18n";
import { ROUTE_PATHS } from "@/lib/index";

import Magnetic from "./Magnetic";

export default function Layout({ children }: { children: React.ReactNode }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isDark, setIsDark] = useState(true);
  const { lang, toggleLang } = useLangStore();

  // Dark mode toggle
  const toggleDark = () => {
    const newDark = !isDark;
    setIsDark(newDark);
    document.documentElement.classList.toggle("dark", newDark);
  };
  const t = translations[lang];
  const location = useLocation();

  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  // Custom cursor logic
  const mouseX = useMotionValue(-100);
  const mouseY = useMotionValue(-100);
  const [isHovering, setIsHovering] = useState(false);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      mouseX.set(e.clientX);
      mouseY.set(e.clientY);
      
      const target = e.target as HTMLElement;
      setIsHovering(!!target.closest("button, a, .cursor-pointer"));
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location]);

  const navLinks = [
    { path: ROUTE_PATHS.HOME, label: t.nav.home },
    { path: ROUTE_PATHS.PORTFOLIO, label: t.nav.portfolio },
    { path: ROUTE_PATHS.ABOUT, label: t.nav.about },
    { path: ROUTE_PATHS.SERVICES, label: t.nav.services },
    { path: ROUTE_PATHS.CONTACT, label: t.nav.contact },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground noise relative overflow-x-hidden">
      {/* Custom Cursor */}
      <motion.div
        className="hidden lg:block fixed top-0 left-0 w-8 h-8 rounded-full border border-primary pointer-events-none z-[9999] mix-blend-difference"
        style={{
          x: mouseX,
          y: mouseY,
          translateX: "-50%",
          translateY: "-50%",
        }}
        animate={{
          scale: isHovering ? 2 : 1,
          backgroundColor: isHovering ? "var(--primary)" : "transparent",
        }}
        transition={{ type: "spring", stiffness: 500, damping: 28, mass: 0.5 }}
      />

      {/* Enhanced Scroll Progress Bar (Space Ruler) */}
      <div className="fixed top-0 left-0 right-0 h-1 z-[60] flex flex-col">
        <motion.div
          className="h-full bg-primary origin-left relative"
          style={{ scaleX }}
        >
          {/* Ticks overlay */}
          <div className="absolute top-0 left-0 w-[100vw] h-4 ruler-ticks opacity-30 text-primary pointer-events-none" />
        </motion.div>
      </div>

      {/* Header */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-black border-b border-white/10 shadow-lg"
            : "bg-black"
        } text-white`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-20">
            {/* Logo */}
            <NavLink to={ROUTE_PATHS.HOME} className="flex items-center gap-3 group">
              <img src="/images/logo.jpg" alt="Logo" className="w-10 h-10 object-contain" />
            </NavLink>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-1">
              {navLinks.map((link) => (
                <NavLink
                  key={link.path}
                  to={link.path}
                  className={({ isActive }) =>
                    `relative px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      isActive
                        ? "text-primary"
                        : "text-white/80 hover:text-white hover:bg-white/10"
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      {link.label}
                      {isActive && (
                        <motion.div
                          layoutId="nav-active"
                          className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ duration: 0.3 }}
                        />
                      )}
                    </>
                  )}
                </NavLink>
              ))}
            </nav>

            {/* Right actions */}
            <div className="flex items-center gap-2">
              {/* Dark mode toggle */}
              <button
                onClick={toggleDark}
                className="p-2 rounded-lg text-white/60 hover:text-white hover:bg-white/10 transition-all duration-200"
                aria-label="Toggle dark mode"
              >
                {isDark ? <Sun size={18} /> : <Moon size={18} />}
              </button>

              {/* Language Toggle */}
              <button
                onClick={toggleLang}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-white/60 hover:text-white hover:bg-white/10 transition-all duration-200"
                aria-label="Switch language"
              >
                <Globe size={16} />
                <span>{t.nav.switchLang}</span>
              </button>

              {/* CTA */}
              <Magnetic>
                <NavLink
                  to={ROUTE_PATHS.CONTACT}
                  className="hidden md:inline-flex items-center px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 active:scale-95 transition-all duration-200 shadow-lg shadow-primary/25"
                >
                  {t.nav.contact}
                </NavLink>
              </Magnetic>

              {/* Mobile menu toggle */}
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                className="md:hidden p-2 rounded-lg hover:bg-white/10 text-white/60 hover:text-white transition-colors"
                aria-label="Toggle menu"
              >
                {menuOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {menuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="md:hidden border-t border-white/10 bg-black/95 backdrop-blur-xl overflow-hidden"
            >
              <div className="px-4 py-4 space-y-1">
                {navLinks.map((link) => (
                  <NavLink
                    key={link.path}
                    to={link.path}
                    className={({ isActive }) =>
                      `block px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                        isActive
                          ? "text-primary bg-primary/10"
                          : "text-white/70 hover:text-white hover:bg-white/10"
                      }`
                    }
                  >
                    {link.label}
                  </NavLink>
                ))}
                <NavLink
                  to={ROUTE_PATHS.CONTACT}
                  className="block mt-2 px-4 py-3 rounded-lg bg-primary text-primary-foreground text-sm font-semibold text-center hover:bg-primary/90 transition-colors"
                >
                  {t.nav.contact}
                </NavLink>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* Page Content */}
      <AnimatePresence mode="wait">
        <motion.main
          key={location.pathname}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
        >
          {children}
        </motion.main>
      </AnimatePresence>

      {/* Footer */}
      <footer className="border-t border-border bg-card/50 mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            {/* Brand */}
            <div>
              <p className="text-sm text-muted-foreground leading-relaxed max-w-xs">
                {lang === "en"
                  ? "Creating iconic brand identities that stand the test of time."
                  : "إنشاء هويات تجارية أيقونية تصمد أمام اختبار الزمن."}
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="font-semibold text-sm text-foreground mb-4">
                {lang === "en" ? "Quick Links" : "روابط سريعة"}
              </h3>
              <ul className="space-y-2">
                {navLinks.map((link) => (
                  <li key={link.path}>
                    <NavLink
                      to={link.path}
                      className="text-sm text-muted-foreground hover:text-primary transition-colors"
                    >
                      {link.label}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h3 className="font-semibold text-sm text-foreground mb-4">
                {lang === "en" ? "Get in Touch" : "تواصل معي"}
              </h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>hany.design77@gmail.com</li>
                <li>{lang === "en" ? "Available worldwide" : "متاح عالمياً"}</li>
                <li className="flex items-center gap-3 pt-2">
                  <Magnetic>
                    <a
                      href="https://wa.me/201015346757"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center hover:bg-primary/20 hover:text-primary transition-colors text-xs font-bold"
                    >
                      <FaWhatsapp size={16} />
                    </a>
                  </Magnetic>
                  <Magnetic>
                    <a
                      href="https://tiktok.com/@mo_lord315"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center hover:bg-primary/20 hover:text-primary transition-colors text-xs font-bold"
                    >
                      <FaTiktok size={14} />
                    </a>
                  </Magnetic>
                  <Magnetic>
                    <a
                      href="https://www.instagram.com/mo1_hany?igsh=cGU4OGd3ZHZoN2hl"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center hover:bg-primary/20 hover:text-primary transition-colors text-xs font-bold"
                    >
                      <FaInstagram size={16} />
                    </a>
                  </Magnetic>
                  <Magnetic>
                    <a
                      href="https://www.linkedin.com/in/hany-design-786110402?utm_source=share_via&utm_content=profile&utm_medium=member_android"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center hover:bg-primary/20 hover:text-primary transition-colors text-xs font-bold"
                    >
                      <FaLinkedinIn size={14} />
                    </a>
                  </Magnetic>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-border pt-8 flex flex-col sm:flex-row items-center justify-center gap-4 text-sm text-muted-foreground text-center">
            <p>
              © 2026 Hany Design. all right reseved
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
